import React, { useEffect, useState, useCallback } from 'react';
import { INK } from '../../Utils/styleTokens';
import { getAuditLog, AUDIT_CATEGORIES, AUDIT_ACTIONS } from '../api';

// Audit Log screen: who changed what across data sources, inventories, the data graph, TDM
// and jobs — newest first, filterable, paged. Admin-only on the server (GET /api/admin/audit),
// so non-admins get a notice instead of a 403.
const PAGE_SIZE = 50;
const EMPTY_FILTERS = { category: '', action: '', user: '', q: '', fromDate: '', toDate: '' };

const AuditLogScreen = ({ isAdmin }) => {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ content: [], totalElements: 0, totalPages: 0 });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    setErr(null);
    try {
      const res = await getAuditLog({
        page,
        size: PAGE_SIZE,
        category: applied.category,
        action: applied.action,
        user: applied.user,
        q: applied.q,
        from: startOfDay(applied.fromDate),
        to: startOfDay(applied.toDate, 1),
      });
      setData(res);
    } catch (e) {
      setErr(e?.message || 'Failed to load the audit log');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, page, applied]);

  useEffect(() => { load(); }, [load]);

  if (!isAdmin) {
    return (
      <div>
        <h1 className='adv-config-screen-title'>Audit Log</h1>
        <div className='adv-config-warning' data-testid='audit-admin-required'>
          Viewing the audit log requires an administrator account.
        </div>
      </div>
    );
  }

  const set = (key) => (e) => setFilters({ ...filters, [key]: e.target.value });
  const apply = (e) => {
    if (e) e.preventDefault();
    setPage(0);
    setApplied(filters);
  };
  const clear = () => {
    setFilters(EMPTY_FILTERS);
    setPage(0);
    setApplied(EMPTY_FILTERS);
  };
  const rows = data.content || [];
  const totalPages = data.totalPages || 0;

  return (
    <div>
      <h1 className='adv-config-screen-title'>Audit Log</h1>
      <p className='adv-config-screen-desc'>
        Who changed what: data sources, inventories, the data graph, test data management and job
        actions, newest first. Jobs started by the platform on a user&apos;s behalf show what started
        them (subset run, schedule, job group, …).
      </p>

      <div className='adv-config-card'>
        <form className='adv-config-toolbar' onSubmit={apply} style={{ flexWrap: 'wrap', gap: 8 }}
          data-testid='audit-filters'>
          <select className='adv-config-select' value={filters.category} onChange={set('category')}
            aria-label='Category' data-testid='audit-filter-category'>
            <option value=''>All categories</option>
            {AUDIT_CATEGORIES.map((c) => <option key={c} value={c}>{label(c)}</option>)}
          </select>
          <select className='adv-config-select' value={filters.action} onChange={set('action')}
            aria-label='Action' data-testid='audit-filter-action'>
            <option value=''>All actions</option>
            {AUDIT_ACTIONS.map((a) => <option key={a} value={a}>{label(a)}</option>)}
          </select>
          <input className='adv-config-search' placeholder='User email' value={filters.user}
            onChange={set('user')} aria-label='User email' data-testid='audit-filter-user' />
          <input className='adv-config-search' placeholder='Search summary or name' value={filters.q}
            onChange={set('q')} aria-label='Search' data-testid='audit-filter-q' />
          <label style={{ fontSize: 12, color: INK[500] }}>
            From{' '}
            <input type='date' className='adv-config-search' value={filters.fromDate}
              onChange={set('fromDate')} data-testid='audit-filter-from' />
          </label>
          <label style={{ fontSize: 12, color: INK[500] }}>
            To{' '}
            <input type='date' className='adv-config-search' value={filters.toDate}
              onChange={set('toDate')} data-testid='audit-filter-to' />
          </label>
          <button type='submit' className='adv-config-btn' data-testid='audit-apply'>Apply</button>
          <button type='button' className='adv-config-btn-secondary adv-config-btn' onClick={clear}>Clear</button>
          <span style={{ flex: 1 }} />
          <button type='button' className='adv-config-btn-secondary adv-config-btn'
            onClick={load} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </form>
        {err && <div className='adv-config-msg-err' style={{ marginBottom: 10 }}>{err}</div>}

        <table className='adv-config-table' data-testid='audit-table'>
          <thead>
            <tr>
              <th style={{ width: 160 }}>When</th>
              <th style={{ width: 180 }}>User</th>
              <th style={{ width: 100 }}>Origin</th>
              <th style={{ width: 110 }}>Category</th>
              <th style={{ width: 100 }}>Action</th>
              <th style={{ width: 200 }}>Entity</th>
              <th>Summary</th>
              <th style={{ width: 70 }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <React.Fragment key={r.id}>
                <tr data-testid={`audit-row-${r.id}`}>
                  <td style={{ fontSize: 12 }}>{formatTime(r.occurredAt)}</td>
                  <td style={{ fontSize: 12 }}>{r.userEmail || (r.origin === 'SYSTEM' ? 'System' : '—')}</td>
                  <td style={{ fontSize: 12 }}>{label(r.origin)}</td>
                  <td style={{ fontSize: 12 }}>{label(r.category)}</td>
                  <td style={{ fontSize: 12 }}>{label(r.action)}</td>
                  <td style={{ fontSize: 12 }}>
                    {r.entityName || '—'}
                    <span style={{ color: INK[400], marginLeft: 6 }}>
                      {label(r.entityType)}{r.entityId ? ` #${r.entityId}` : ''}
                    </span>
                  </td>
                  <td style={{ fontSize: 12 }}>{r.summary}</td>
                  <td>
                    <button type='button' className='adv-config-btn-secondary adv-config-btn'
                      onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
                      {expanded === r.id ? 'Hide' : 'Detail'}
                    </button>
                  </td>
                </tr>
                {expanded === r.id && (
                  <tr>
                    <td colSpan={8} style={{ fontSize: 12, color: INK[700] }} data-testid={`audit-detail-${r.id}`}>
                      <Detail name='Origin reference' value={r.originRef} />
                      <Detail name='User id' value={r.userId} />
                      <Detail name='Data source id' value={r.dataSourceId} />
                      <Detail name='Client IP' value={r.clientIp} />
                      <Detail name='Entry id' value={r.id} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} style={{ color: INK[500] }}>No audit entries match.</td></tr>
            )}
          </tbody>
        </table>

        <div className='adv-config-toolbar' style={{ marginTop: 10, marginBottom: 0 }}>
          <span style={{ fontSize: 12, color: INK[500] }} data-testid='audit-page-info'>
            {totalPages > 0 ? `Page ${page + 1} of ${totalPages}` : 'Page 0 of 0'}
            {' · '}{(data.totalElements || 0).toLocaleString()} entries
          </span>
          <span style={{ flex: 1 }} />
          <button type='button' className='adv-config-btn-secondary adv-config-btn'
            onClick={() => setPage(page - 1)} disabled={loading || page === 0} data-testid='audit-prev'>
            Previous
          </button>
          <button type='button' className='adv-config-btn-secondary adv-config-btn'
            onClick={() => setPage(page + 1)} disabled={loading || page + 1 >= totalPages}
            data-testid='audit-next'>
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

const Detail = ({ name, value }) => (
  <div>
    <span style={{ color: INK[500], display: 'inline-block', width: 130 }}>{name}</span>
    <span className='adv-config-mono'>{value === null || value === undefined || value === '' ? '—' : String(value)}</span>
  </div>
);

// DATA_SOURCE → "Data source", TDM_SUBSET_RUN → "TDM subset run".
export function label(v) {
  if (!v) return '—';
  const words = String(v).toLowerCase().split('_');
  return words
    .map((w, i) => (w === 'tdm' ? 'TDM' : i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
}

// A yyyy-mm-dd date input → the ISO instant of that local midnight, plus `addDays` days.
export function startOfDay(date, addDays = 0) {
  if (!date) return null;
  const [y, m, d] = date.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d + addDays).toISOString();
}

function formatTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
}

export default AuditLogScreen;
